/**
 * dragHandle — drag-to-reorder support for Topic / ActionItem /
 * Bullet NodeViews.
 *
 * Why this is non-trivial:
 *   - The drag-handle button is a widget rendered inside the
 *     NodeView, NOT part of ProseMirror's document tree. PM's
 *     built-in dragstart listener on view.dom doesn't recognise
 *     widget elements as drag sources, so the automatic path
 *     never sets up dataTransfer.
 *   - React's `onDragStart` prop fires via React's synthetic
 *     event delegation at the document root — after PM's native
 *     listener on view.dom has already bubbled past. So calling
 *     `event.stopPropagation()` from a React handler is too late
 *     to prevent PM from interfering.
 *
 * Solution (`useDragHandle` below):
 *   - Attach a NATIVE dragstart listener directly to the button
 *     via useEffect. The listener fires in the target phase,
 *     before PM's view.dom listener.
 *   - stopPropagation() on the native event prevents PM's
 *     listener from running at all — we own this drag end-to-end.
 *   - Build the slice + serialize via DOMSerializer (don't depend
 *     on view.serializeForClipboard which may be missing on
 *     older PM versions).
 *   - Stamp dataTransfer with html / text / effectAllowed.
 *   - Stash the slice + the SOURCE RANGE (from / to) on
 *     view.dragging. Our custom `handleDrop` in
 *     TiptapStructuredEditor reads from/to directly to delete the
 *     source — we no longer rely on PM's default drop reading a
 *     NodeSelection at drop time. That removes the timing
 *     fragility around dispatching a NodeSelection during
 *     dragstart (which used to fight the BubbleMenu and the React
 *     NodeView re-render cycle, leaving the drop silently broken
 *     for a majority of positions).
 */
import { useEffect, type RefObject } from 'react';
import type { Editor } from '@tiptap/react';
import { DOMSerializer, type Slice } from '@tiptap/pm/model';

export interface DragPayload {
  slice: Slice;
  move: boolean;
  /** Absolute PM position where the dragged node starts. Used by
   *  the custom drop handler to delete the source range on a
   *  "move" drop. */
  from: number;
  /** from + node.nodeSize. */
  to: number;
}

interface ViewWithDragging {
  dragging?: DragPayload | null;
}

/**
 * React hook that wires a drag-handle button to the current
 * NodeView's parent node. Pass the button's ref + the editor +
 * getPos (the function Tiptap provides on NodeViewProps that
 * returns the node's PM position).
 */
export function useDragHandle(
  ref: RefObject<HTMLElement | null>,
  editor: Editor,
  getPos: () => number | undefined,
): void {
  useEffect(() => {
    const button = ref.current;
    if (!button) return;

    const onDragStart = (event: DragEvent) => {
      try {
        const pos = getPos();
        if (typeof pos !== 'number' || pos < 0) return;

        const view = editor.view;
        const doc = view.state.doc;
        if (pos >= doc.content.size) return;

        const node = doc.nodeAt(pos);
        if (!node) return;

        // Stop PM's listener on view.dom from running so we own
        // the drag setup end-to-end.
        event.stopPropagation();

        // Build the slice for the dragged node.
        const slice = doc.slice(pos, pos + node.nodeSize);

        // Serialize to DOM via DOMSerializer (works regardless of
        // whether the PM version exposes view.serializeForClipboard).
        let html = '';
        const text = node.textContent || ' ';
        try {
          const serializer = DOMSerializer.fromSchema(view.state.schema);
          const fragment = serializer.serializeFragment(slice.content);
          const wrap = document.createElement('div');
          wrap.appendChild(fragment);
          html = wrap.innerHTML;
        } catch {
          // Fall through with empty html — text is still set.
        }

        // dataTransfer must have SOME data or browsers cancel the
        // drag silently. Always set text/plain as a floor.
        if (event.dataTransfer) {
          event.dataTransfer.clearData();
          if (html) event.dataTransfer.setData('text/html', html);
          event.dataTransfer.setData('text/plain', text);
          event.dataTransfer.effectAllowed = 'move';

          // Custom drag image — point the floating ghost at the
          // whole NodeView wrapper rather than just the handle
          // button. Without this the browser snapshots only the
          // tiny [⋮⋮] button, so the user has no visual sense of
          // what's being dragged. The wrapper is the closest
          // ancestor that carries a [data-node] attribute, set by
          // every NodeView's renderHTML / NodeViewWrapper.
          const wrapper = button.closest('[data-node]') as HTMLElement | null;
          if (wrapper) {
            const rect = wrapper.getBoundingClientRect();
            event.dataTransfer.setDragImage(
              wrapper,
              Math.max(0, event.clientX - rect.left),
              Math.max(0, event.clientY - rect.top),
            );
            // Source-dim: tag the wrapper so CSS can fade it
            // while the drag is in flight. Cleared in onDragEnd.
            wrapper.classList.add('tt-dragging-source');
          }
        }

        // Stash the slice + source range on view so our custom
        // drop handler can pick it up on drop. The same `dragging`
        // field is what PM uses internally; consuming it ourselves
        // means PM's default dragstart never runs (because we
        // stopPropagation above) and PM's default drop never sees
        // a slice (because we clear it in handleDrop) — so the
        // entire drag end-to-end is owned by our own code.
        (view as unknown as ViewWithDragging).dragging = {
          slice,
          move: true,
          from: pos,
          to: pos + node.nodeSize,
        };

        // NOTE: we deliberately do NOT dispatch a NodeSelection
        // over the source node anymore. The previous version did
        // that so PM's default drop handler knew what to delete on
        // a "move" drop, but it caused two problems:
        //   (1) The transaction re-rendered the React NodeView
        //       mid-dragstart, occasionally invalidating the
        //       in-flight drag.
        //   (2) NodeSelection is non-empty, which would surface
        //       the BubbleMenu over the cursor and intercept
        //       dragover events, breaking the drop entirely.
        // Now the source range lives on view.dragging.from/to and
        // our custom drop handler in TiptapStructuredEditor reads
        // it directly — no selection round-trip needed.
      } catch (err) {
        // Drag bailed for some unexpected reason — let it cancel
        // gracefully rather than throwing into the browser's
        // drag pipeline.
        console.error('[dragHandle] dragstart failed', err);
      }
    };

    // dragend cleanup — if PM's dragend listener didn't fire (e.g.
    // because of stopPropagation issues higher up the chain), we
    // clear view.dragging ourselves so subsequent interactions
    // don't see stale drag state. Also clear the source-dim
    // class from any wrapper that still carries it.
    const onDragEnd = () => {
      const draggingView = editor.view as unknown as ViewWithDragging;
      if (draggingView.dragging) draggingView.dragging = null;
      // querySelectorAll is cheap (rarely matches more than one
      // element) and resilient to edge cases where the source
      // node has been re-rendered between dragstart and dragend.
      document
        .querySelectorAll('.tt-dragging-source')
        .forEach((el) => el.classList.remove('tt-dragging-source'));
    };

    button.addEventListener('dragstart', onDragStart);
    button.addEventListener('dragend', onDragEnd);
    return () => {
      button.removeEventListener('dragstart', onDragStart);
      button.removeEventListener('dragend', onDragEnd);
    };
  }, [ref, editor, getPos]);
}
